import type {
  IWorkJourneyManagementAttentionListParams,
  IWorkJourneyManagementAttentionListResponse,
  IWorkJourneyManagementCalendarCell,
  IWorkJourneyManagementCalendarListParams,
  IWorkJourneyManagementCalendarListResponse,
  IWorkJourneyManagementCalendarMonthSummary,
  IWorkJourneyManagementDayReportDetailParams,
  IWorkJourneyManagementDayReportDetailResponse,
  IWorkJourneyManagementDayReportParams,
  IWorkJourneyManagementDayReportResponse,
  IWorkJourneyManagementJourneysListParams,
  IWorkJourneyManagementJourneysListResponse,
  IWorkJourneyManagementMatrixListParams,
  IWorkJourneyManagementMatrixListResponse,
  IWorkJourneyConfigStaffingSummary,
  IWorkJourneyReplacementCandidate,
  IWorkJourneyReplacementCandidatesListParams,
  IWorkJourneyReplacementCandidatesListResponse,
} from '@src/models/interfaces/workJourneyManagement.interface';
import { WorkJourneyManagementMapper } from '@src/models/mappers/WorkJourneyManagementMapper';
import { buildWorkJourneyCalendarGrid } from '@src/packages/workJourney/utils/buildWorkJourneyCalendarGrid';
import type { ReplacementJourneyImpact } from '@src/packages/workJourney/utils/evaluateReplacementJourneyImpact';
import { evaluateReplacementJourneyImpact } from '@src/packages/workJourney/utils/evaluateReplacementJourneyImpact';
import type {
  IWorkJourneyConfigImpactSnapshot,
  PlanMonthlyJourneyCallableParams,
} from '@src/repositories/WorkJourneyManagementRepository';
import WorkJourneyManagementRepository from '@src/repositories/WorkJourneyManagementRepository';
import safeExecute from '@src/util/safeExecute';

interface IListCalendarResponse extends IWorkJourneyManagementCalendarListResponse {
  cells: IWorkJourneyManagementCalendarCell[];
  summary: IWorkJourneyManagementCalendarMonthSummary;
}

export type EvaluateCoverReplacementImpactParams = {
  organizationId: string;
  coverDate: string;
  coverWorkJourneyConfigId: string;
  candidate: IWorkJourneyReplacementCandidate;
};

class WorkJourneyManagementService {
  private repository: WorkJourneyManagementRepository;

  constructor() {
    this.repository = new WorkJourneyManagementRepository();
  }

  /** @description List attention items for work journey management */
  async listAttentionItems(
    params: IWorkJourneyManagementAttentionListParams,
  ): Promise<IWorkJourneyManagementAttentionListResponse> {
    return safeExecute(
      async () => {
        const response = await this.repository.listAttentionItems(params);
        return WorkJourneyManagementMapper.fromAttentionListResponse(response);
      },
      'WorkJourneyManagementService.listAttentionItems',
      params,
    );
  }

  /** @description List journeys with month metrics for work journey management */
  async listJourneys(
    params: IWorkJourneyManagementJourneysListParams,
  ): Promise<IWorkJourneyManagementJourneysListResponse> {
    return safeExecute(
      async () => this.repository.listJourneys(params),
      'WorkJourneyManagementService.listJourneys',
      params,
    );
  }

  /** @description List calendar days for work journey management */
  async listCalendar(
    params: IWorkJourneyManagementCalendarListParams,
  ): Promise<IListCalendarResponse> {
    return safeExecute(
      async () => {
        const response = await this.repository.listCalendar(params);
        const cells = buildWorkJourneyCalendarGrid(params.month, params.year, response.days);
        return { ...response, cells };
      },
      'WorkJourneyManagementService.listCalendar',
      params,
    );
  }

  /** @description Past-day report: paginated journeys + day totals. */
  async listDayReport(
    params: IWorkJourneyManagementDayReportParams,
  ): Promise<IWorkJourneyManagementDayReportResponse> {
    return safeExecute(
      async () => this.repository.listDayReport(params),
      'WorkJourneyManagementService.listDayReport',
      params,
    );
  }

  /** @description Past-day report users for one journey (lazy expand). */
  async listDayReportDetail(
    params: IWorkJourneyManagementDayReportDetailParams,
  ): Promise<IWorkJourneyManagementDayReportDetailResponse> {
    return safeExecute(
      async () => this.repository.listDayReportDetail(params),
      'WorkJourneyManagementService.listDayReportDetail',
      params,
    );
  }

  /** @description Users×days month matrix (attendance_dashboard + Firestore exceptions). */
  async listMonthMatrix(
    params: IWorkJourneyManagementMatrixListParams,
  ): Promise<IWorkJourneyManagementMatrixListResponse> {
    return safeExecute(
      async () => this.repository.listMonthMatrix(params),
      'WorkJourneyManagementService.listMonthMatrix',
      params,
    );
  }

  /**
   * Lists users available to cover an absence, including those without a current journey.
   * Uses the ES users index via `listCandidates` CF.
   */
  async listReplacementCandidates(
    params: IWorkJourneyReplacementCandidatesListParams,
  ): Promise<IWorkJourneyReplacementCandidatesListResponse> {
    return safeExecute(
      async () => {
        const response = await this.repository.listReplacementCandidates(params);
        return WorkJourneyManagementMapper.fromReplacementCandidatesResponse(response);
      },
      'WorkJourneyManagementService.listReplacementCandidates',
      params,
    );
  }

  /**
   * Snapshot of a work journey config used by cover-replacement flows
   * (required members + embedded work shift).
   */
  async getWorkJourneyConfigSnapshot(
    organizationId: string,
    workJourneyConfigId: string,
  ): Promise<IWorkJourneyConfigImpactSnapshot | null> {
    return safeExecute(
      async () =>
        this.repository.getWorkJourneyConfigSnapshot(organizationId, workJourneyConfigId),
      'WorkJourneyManagementService.getWorkJourneyConfigSnapshot',
      { organizationId, workJourneyConfigId },
    );
  }

  async getWorkJourneyMembersCount(
    organizationId: string,
    workJourneyConfigId: string,
  ): Promise<number> {
    return safeExecute(
      async () =>
        this.repository.getWorkJourneyMembersCount(organizationId, workJourneyConfigId),
      'WorkJourneyManagementService.getWorkJourneyMembersCount',
      { organizationId, workJourneyConfigId },
    );
  }

  async listWorkJourneyConfigStaffing(
    organizationId: string,
    workJourneyConfigIds: string[],
  ): Promise<IWorkJourneyConfigStaffingSummary[]> {
    return safeExecute(
      async () => {
        const uniqueConfigIds = Array.from(
          new Set(workJourneyConfigIds.map(id => id.trim()).filter(Boolean)),
        );

        return Promise.all(
          uniqueConfigIds.map(async workJourneyConfigId => {
            const [config, assignedMembers] = await Promise.all([
              this.repository.getWorkJourneyConfigSnapshot(
                organizationId,
                workJourneyConfigId,
              ),
              this.repository.getWorkJourneyMembersCount(
                organizationId,
                workJourneyConfigId,
              ),
            ]);

            return {
              workJourneyConfigId,
              assignedMembers,
              requiredMembers: config?.requiredMembers ?? 0,
            };
          }),
        );
      },
      'WorkJourneyManagementService.listWorkJourneyConfigStaffing',
      { organizationId, workJourneyConfigIds },
    );
  }

  /**
   * Evaluates whether selecting a replacement candidate impacts their current journey
   * (schedule/day overlap with the journey being covered).
   */
  async evaluateCoverReplacementImpact(
    params: EvaluateCoverReplacementImpactParams,
  ): Promise<ReplacementJourneyImpact> {
    return safeExecute(
      async () => {
        const { organizationId, coverDate, coverWorkJourneyConfigId, candidate } = params;
        const displayName =
          `${candidate.firstName} ${candidate.lastName}`.trim() || candidate.displayName;

        if (!candidate.currentWorkJourney) {
          return evaluateReplacementJourneyImpact({
            userDisplayName: displayName,
            coverDate,
            coverWorkShift: null,
            currentWorkJourney: null,
            currentWorkShift: null,
            currentRequiredMembers: 0,
            currentAssignedMembers: 0,
          });
        }

        const [coverConfig, currentConfig, assignedMembers] = await Promise.all([
          this.repository.getWorkJourneyConfigSnapshot(organizationId, coverWorkJourneyConfigId),
          this.repository.getWorkJourneyConfigSnapshot(
            organizationId,
            candidate.currentWorkJourney.workJourneyConfigId,
          ),
          this.repository.getWorkJourneyMembersCount(
            organizationId,
            candidate.currentWorkJourney.workJourneyConfigId,
          ),
        ]);

        return evaluateReplacementJourneyImpact({
          userDisplayName: displayName,
          coverDate,
          coverWorkShift: coverConfig?.workShift ?? null,
          currentWorkJourney: {
            workJourneyConfigId: candidate.currentWorkJourney.workJourneyConfigId,
            workJourneyConfigName:
              currentConfig?.name ?? candidate.currentWorkJourney.workJourneyConfigName,
          },
          currentWorkShift: currentConfig?.workShift ?? null,
          currentRequiredMembers: currentConfig?.requiredMembers ?? 0,
          currentAssignedMembers: assignedMembers,
        });
      },
      'WorkJourneyManagementService.evaluateCoverReplacementImpact',
      params,
    );
  }

  async planMonthlyJourney(params: PlanMonthlyJourneyCallableParams): Promise<{
    success: boolean;
    pendingNotices: number;
    message?: string;
  }> {
    return safeExecute(
      () => this.repository.planMonthlyJourney(params),
      'WorkJourneyManagementService.planMonthlyJourney',
      params,
    );
  }

  /**
   * Creates (or returns) an attendance record for a blank past/today matrix cell
   * so the supervisor can mark presence or leave.
   */
  async createMatrixAttendance(params: {
    organizationId: string;
    workJourneyConfigId: string;
    userId: string;
    date: string;
  }): Promise<{ attendanceRecordId: string; created: boolean }> {
    return safeExecute(
      () => this.repository.createMatrixAttendance(params),
      'WorkJourneyManagementService.createMatrixAttendance',
      params,
    );
  }

  async rejectMembershipChangeByToken(token: string): Promise<{
    ok: boolean;
    status: number;
    data?: { error?: string; errorCode?: string };
  }> {
    return safeExecute(
      () => this.repository.rejectMembershipChangeByToken(token),
      'WorkJourneyManagementService.rejectMembershipChangeByToken',
      { tokenPresent: !!token },
    );
  }

  /** @description Dismiss a cancelled SHIFT_REPLACEMENT from attention */
  async ignoreRejectedShiftChange(params: {
    organizationId: string;
    eventId: string;
  }): Promise<{ success: boolean; message?: string }> {
    return safeExecute(
      () => this.repository.ignoreRejectedShiftChange(params),
      'WorkJourneyManagementService.ignoreRejectedShiftChange',
      params,
    );
  }

  /** @description Re-apply coverage and notify the substitute again */
  async resendRejectedShiftChange(params: {
    organizationId: string;
    eventId: string;
  }): Promise<{ success: boolean; message?: string }> {
    return safeExecute(
      () => this.repository.resendRejectedShiftChange(params),
      'WorkJourneyManagementService.resendRejectedShiftChange',
      params,
    );
  }
}

export default WorkJourneyManagementService;
